import { useTranslation } from 'react-i18next'
import { useRemoveDoNotContact, useSetDoNotContact } from '@/hooks/useApi'
import { toast } from '@/hooks/use-toast'

/**
 * Setting and removing the "do not contact" mark of a contact (#372), with
 * the outcome told to the user: used by the contact page and by the reply
 * notification.
 */
export function useDoNotContact() {
  const { t } = useTranslation('common')
  const setMark = useSetDoNotContact()
  const removeMark = useRemoveDoNotContact()

  const failed = () =>
    toast({ title: t('notifications.doNotContact.error'), variant: 'destructive' })

  const mark = (contactId: string, onDone?: () => void) =>
    setMark.mutate(contactId, {
      onSuccess: (result) => {
        toast({
          title: t('notifications.doNotContact.markedToast'),
          description:
            result.stopped_enrollments > 0
              ? t('notifications.doNotContact.markedToastDescription', {
                  count: result.stopped_enrollments,
                })
              : t('notifications.doNotContact.markedToastNoneStopped'),
          variant: 'success',
        })
        onDone?.()
      },
      onError: failed,
    })

  const unmark = (contactId: string, onDone?: () => void) =>
    removeMark.mutate(contactId, {
      onSuccess: () => {
        toast({
          title: t('notifications.doNotContact.removedToast'),
          description: t('notifications.doNotContact.removedToastDescription'),
          variant: 'success',
        })
        onDone?.()
      },
      onError: failed,
    })

  return { mark, unmark, isPending: setMark.isPending || removeMark.isPending }
}
